import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { auth } from './firebase';
import { sendSignInLinkToEmail, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import './AuthModal.css';

export default function AuthModal({ onClose }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState('');
  const [sentTo, setSentTo] = useState('');
  const [error, setError] = useState('');
  const box = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    const previous = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    box.current.querySelector('input')?.focus();
    function keyboard(e) {
      if (e.key === 'Escape') closeRef.current();
      if (e.key !== 'Tab') return;
      const elements = Array.from(box.current.querySelectorAll('button:not(:disabled),input:not(:disabled),a[href]'));
      const first = elements[0], last = elements[elements.length - 1];
      if (e.shiftKey && (document.activeElement === first || !box.current.contains(document.activeElement))) { e.preventDefault(); last?.focus(); }
      else if (!e.shiftKey && (document.activeElement === last || !box.current.contains(document.activeElement))) { e.preventDefault(); first?.focus(); }
    }
    document.addEventListener('keydown', keyboard);
    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener('keydown', keyboard);
      previous?.focus();
    };
  }, []);
  async function emailLogin(event) {
    event.preventDefault();
    if (busy) return;
    setBusy('email'); setError('');
    try {
      const address = email.trim();
      await sendSignInLinkToEmail(auth, address, { url: 'https://www.avisamesibaja.com/confirm-email', handleCodeInApp: true });
      localStorage.setItem('emailForSignIn', address);
      setSentTo(address);
    } catch {
      setError('No hemos podido enviar el enlace. Comprueba tu email y vuelve a intentarlo.');
    } finally { setBusy(''); }
  }
  async function googleLogin() {
    if (busy) return;
    setBusy('google'); setError('');
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      await signInWithPopup(auth, provider);
      if (window.gtag) window.gtag('event', 'login', { method: 'google' });
      onClose();
      if (location.pathname !== '/') navigate('/', { replace: true });
    } catch {
      setError('No se ha completado el acceso con Google. Vuelve a intentarlo o entra con tu email.');
    } finally { setBusy(''); }
  }
  return createPortal(
    <div className="modal-backdrop" onClick={e => { if(e.target === e.currentTarget) onClose(); }}>
      <section className="auth-modal-box" ref={box} role="dialog" aria-modal="true" aria-labelledby="auth-title" aria-describedby="auth-description">
        <button type="button" className="auth-close" onClick={onClose} aria-label="Cerrar acceso">×</button>
        <span className="auth-eyebrow">TUS ALERTAS, A MANO</span>
        <h2 id="auth-title">{sentTo ? 'Revisa tu correo' : 'Accede a tus alertas'}</h2>
        <p id="auth-description">{sentTo ? 'Abre el enlace que te hemos enviado para entrar en tu cuenta.' : 'Entra con tu email o Google. No necesitas contraseña.'}</p>
        {sentTo ? <div role="status" className="auth-success"><strong>{sentTo}</strong><p>¿No lo encuentras? Comprueba la carpeta de spam.</p><button className="auth-secondary" onClick={() => setSentTo('')}>Cambiar email o reenviar</button></div> : <>
          <form onSubmit={emailLogin}>
            <label htmlFor="auth-email">Tu email</label>
            <input id="auth-email" type="email" autoComplete="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="tu@email.com" />
            <button type="submit" className="auth-primary" disabled={!!busy}>{busy === 'email' ? 'Enviando enlace…' : 'Recibir enlace de acceso'}</button>
          </form>
          <div className="auth-divider">o</div>
          <button type="button" className="auth-secondary" disabled={!!busy} onClick={googleLogin}>{busy === 'google' ? 'Conectando…' : 'Continuar con Google'}</button>
        </>}
        {error && <p className="auth-error" role="alert">{error}</p>}
      </section>
    </div>, document.body
  );
}
